import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import {
  Plus,
  MagnifyingGlass as Search,
  FunnelSimple as Filter,
  UploadSimple as Upload,
  DownloadSimple as Download,
  FileText,
  FilePdf,
  FileDoc,
  Folder,
  FolderOpen,
  CaretRight,
  CaretDown,
  ListBullets,
  SquaresFour,
  ClockCounterClockwise,
  PaperPlaneTilt,
  Lock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RowActions } from '@/components/shared/RowActions';
import { UploadDocumentModal } from '@/components/shared/UploadDocumentModal';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { DocumentVersionsModal } from '@/components/shared/DocumentVersionsModal';
import { EditDocumentModal } from '@/components/shared/EditDocumentModal';
import { SendDocumentEmailModal } from '@/components/shared/SendDocumentEmailModal';
import { useDocumentsStore } from '@/lib/documentsStore';
import { fetchDocuments as apiFetchDocuments, deleteDocument, type DocFile } from '@/lib/documentsApi';
import { useDebounce } from '@/lib/useDebounce';

function FileIcon({ kind }: { kind: string }) {
  if (kind === 'pdf') return <FilePdf className="h-5 w-5 text-red-600 dark:text-red-400" weight="fill" />;
  if (kind === 'doc') return <FileDoc className="h-5 w-5 text-blue-600 dark:text-blue-400" weight="fill" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
}

export default function DocumentsPage() {
  const [view, setView] = useState<'list' | 'grid'>('list');
  const storeFiles = useDocumentsStore((s) => s.files);
  const storeFolders = useDocumentsStore((s) => s.folders);
  const fetchDocuments = useDocumentsStore((s) => s.fetchDocuments);
  const fetchFolders = useDocumentsStore((s) => s.fetchFolders);
  const storeError = useDocumentsStore((s) => s.error);

  useEffect(() => { fetchDocuments(); fetchFolders(); }, [fetchDocuments, fetchFolders]);

  // Build folder tree from API folders
  const matterFolders = storeFolders.map((f) => ({ id: f.id, name: f.name, count: f.count }));
  const initialTree = [
    { id: 'r1', name: 'All matters', open: true, children: matterFolders.length > 0 ? matterFolders : [{ id: 'all', name: 'All documents', count: storeFiles.length }] },
  ];

  const [tree, setTree] = useState(initialTree);
  const [active, setActive] = useState('r1');
  const [folderSearch, setFolderSearch] = useState('');
  const [fileSearch, setFileSearch] = useState('');
  const debouncedFileSearch = useDebounce(fileSearch.trim(), 300);
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [versionsDoc, setVersionsDoc] = useState<{ id: string; name: string } | null>(null);
  const [editDoc, setEditDoc] = useState<DocFile | null>(null);
  const [sendEmailDoc, setSendEmailDoc] = useState<{ id: string; name: string; matterName?: string } | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isChambersAdmin = user?.memberType === 'admin' || user?.memberType === 'staff';
  const basePath = isClerk ? '/clerk' : '/admin';

  // Update tree when folders load
  useEffect(() => {
    if (storeFolders.length > 0) {
      setTree((t) => t.map((r) => r.id === 'r1' ? { ...r, children: storeFolders.map((f) => ({ id: f.id, name: f.name, count: f.count })) } : r));
    }
  }, [storeFolders]);

  const docSearchQ = useQuery({
    queryKey: ['documents-search', debouncedFileSearch],
    queryFn: () => apiFetchDocuments(undefined, debouncedFileSearch),
    enabled: debouncedFileSearch.length >= 2,
    staleTime: 30_000,
  });

  const baseFiles = debouncedFileSearch.length >= 2
    ? (docSearchQ.data ?? [])
    : storeFiles;

  const filteredFiles = kindFilter
    ? baseFiles.filter((f) => f.kind === kindFilter)
    : baseFiles;

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Documents"
        subtitle="Briefs, pleadings, opinions and case papers — searchable across the chambers"
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>Upload</Button>
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => {
              const name = `New folder ${(tree[0]?.children.length ?? 0) + 1}`;
              setTree(t => t.map((r, i) => i === 0 ? { ...r, children: [...r.children, { id: `f-new-${Date.now()}`, name, count: 0 }] } : r));
              toast.success(`Folder "${name}" created`);
            }}>New folder</Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Folder tree */}
        <Card className="h-fit min-w-0">
          <div className="border-b border-border p-3">
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search folders…"
                value={folderSearch}
                onChange={(e) => setFolderSearch(e.target.value)}
                className="h-8 flex-1 bg-transparent text-xs placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-2 lg:max-h-none lg:overflow-y-visible">
            {tree.map((root) => (
              <div key={root.id} className="mb-1">
                <button
                  onClick={() =>
                    setTree((t) => t.map((r) => (r.id === root.id ? { ...r, open: !r.open } : r)))
                  }
                  className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  {root.open ? <CaretDown className="h-3 w-3" /> : <CaretRight className="h-3 w-3" />}
                  {root.name}
                </button>
                {root.open && (
                  <div className="ml-3 space-y-0.5">
                    {root.children
                      .filter((c) => !folderSearch.trim() || c.name.toLowerCase().includes(folderSearch.trim().toLowerCase()))
                      .map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setActive(c.id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition ${
                          active === c.id
                            ? 'bg-brand-50 text-brand-700 dark:text-brand-300'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-1.5">
                          {active === c.id ? (
                            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-brand-400" weight="fill" />
                          ) : (
                            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate">{c.name}</span>
                        </div>
                        <span className="tabular text-[10px] text-muted-foreground">{c.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* File list */}
        <div className="min-w-0">
          <Card className="mb-4 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search documents — name, description, keyword…"
                  className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                  value={fileSearch}
                  onChange={(e) => setFileSearch(e.target.value)}
                />
                {docSearchQ.isFetching && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                )}
              </div>
              <Button
                variant={filtersOpen || kindFilter ? 'primary' : 'outline'}
                size="md"
                leftIcon={<Filter className="h-4 w-4" />}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                Filters{kindFilter ? ' · 1' : ''}
              </Button>
              <div className="flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
                <button
                  onClick={() => setView('list')}
                  className={`rounded-sm p-1.5 ${view === 'list' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <ListBullets className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setView('grid')}
                  className={`rounded-sm p-1.5 ${view === 'grid' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  <SquaresFour className="h-4 w-4" />
                </button>
              </div>
            </div>
            {filtersOpen && (
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">Type</span>
                  <div className="flex flex-wrap items-center gap-1">
                    {(['All', 'Brief', 'Pleading', 'Opinion', 'Order', 'Correspondence', 'Evidence', 'Judgment', 'Draft', 'Fee Note', 'Contract', 'Other'] as const).map((v) => (
                      <button
                        key={v}
                        onClick={() => setKindFilter(v === 'All' ? null : v)}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          (v === 'All' && kindFilter === null) || kindFilter === v
                            ? 'bg-brand-600 text-white'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                {kindFilter && (
                  <button
                    onClick={() => setKindFilter(null)}
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </Card>

          {storeError && !debouncedFileSearch ? (
            // A failed load is not an empty library.
            <Card className="py-12 text-center">
              <p className="text-sm font-medium text-foreground">Documents could not be loaded</p>
              <p className="mt-1 text-xs text-muted-foreground">This is a connection problem, not missing documents.</p>
              <Button className="mt-4" variant="outline" size="sm" onClick={() => { fetchDocuments(); fetchFolders(); }}>
                Try again
              </Button>
            </Card>
          ) : view === 'list' ? (
            <Card>
              {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-10 whitespace-nowrap">Actions</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Matter</th>
                      <th>Size</th>
                      <th>Uploaded by</th>
                      <th>Date</th>
                      <th className="w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFiles.map((f) => (
                      <tr key={f.id} className="cursor-pointer">
                        <td><FileIcon kind={f.icon} /></td>
                        <td className="font-medium text-foreground">{f.name}</td>
                        <td><Badge>{f.kind}</Badge></td>
                        <td>
                          <button
                            className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                            onClick={(e) => { e.stopPropagation(); if (f.caseId) navigate(`${basePath}/matters/${f.caseId}`); }}
                          >
                            {f.case}
                          </button>
                        </td>
                        <td className="text-xs text-muted-foreground">{f.size}</td>
                        <td>{f.by}</td>
                        <td className="text-xs text-muted-foreground">{f.uploaded}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Version history" onClick={(e) => { e.stopPropagation(); setVersionsDoc({ id: f.id, name: f.name }); }}>
                              <ClockCounterClockwise className="h-4 w-4" />
                            </button>
                            <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Send by email" onClick={(e) => { e.stopPropagation(); setSendEmailDoc({ id: f.id, name: f.name, matterName: f.case || undefined }); }}>
                              <PaperPlaneTilt className="h-4 w-4" />
                            </button>
                            {f.downloadUrl ? (
                              <a
                                href={f.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                title="Download"
                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            ) : (
                              <button className="rounded p-1 text-muted-foreground opacity-40" title="No download available" disabled>
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                            <RowActions
                              onView={() => setPreviewDocId(f.id)}
                              onEdit={() => setEditDoc(f)}
                              editLabel="Edit"
                              onDelete={async () => {
                                try {
                                  await deleteDocument(f.id);
                                  toast.success(`${f.name} deleted`);
                                  fetchDocuments();
                                } catch {
                                  toast.error('Could not delete document — please try again');
                                }
                              }}
                              deleteConfirm={{
                                title: 'Delete document?',
                                message: `Delete “${f.name}”? This cannot be undone.`,
                                confirmLabel: 'Delete',
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            ) : filteredFiles.length === 0 && isChambersAdmin ? (
              /*
               * A chambers admin sees only `chambers` and `client_visible` documents — never
               * `case_team` (counsel instructions, proofs, strategy) or `barrister_only`. That
               * exclusion is deliberate and is NOT relaxed here: an administrator is not on the
               * case team.
               *
               * What was wrong was the silence. Because matter-linked documents default to
               * `case_team`, this page returns nothing for an admin essentially always, and an
               * unexplained empty grid reads as a broken feature rather than a confidentiality
               * boundary doing its job.
               */
              <Card className="py-12 text-center">
                <Lock className="mx-auto h-9 w-9 text-muted-foreground/40" weight="duotone" />
                <p className="mt-3 text-sm font-medium text-foreground">No chambers-wide documents</p>
                <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
                  Documents filed to a matter are confidential to the case team — counsel and the
                  clerk assigned to that matter. Administrators see only documents shared
                  chambers-wide or with the client. This is a confidentiality boundary, not a
                  missing feature.
                </p>
              </Card>
            ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredFiles.map((f) => (
                <Card key={f.id} className="cursor-pointer p-4 transition hover:border-brand-300 hover:shadow-pop">
                  <div className="mb-3 flex h-28 items-center justify-center rounded-md bg-muted/40">
                    <FileIcon kind={f.icon} />
                  </div>
                  <div className="truncate text-sm font-medium text-foreground">{f.name}</div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{f.size}</span>
                    <Badge>{f.kind}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={() => fetchDocuments()}
      />

      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => setPreviewDocId(null)}
      />

      {editDoc && (
        <EditDocumentModal
          document={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={() => fetchDocuments()}
        />
      )}

      {versionsDoc && (
        <DocumentVersionsModal
          documentId={versionsDoc.id}
          documentName={versionsDoc.name}
          onClose={() => setVersionsDoc(null)}
        />
      )}

      {sendEmailDoc && (
        <SendDocumentEmailModal
          documentId={sendEmailDoc.id}
          documentName={sendEmailDoc.name}
          matterName={sendEmailDoc.matterName}
          onClose={() => setSendEmailDoc(null)}
          onSent={() => setSendEmailDoc(null)}
        />
      )}
    </div>
  );
}
